<template>
  <div v-show="showComments" ref="intersectionRef" class="article-comments">
    <div class="header">
      <h2>Комментарии</h2>
      <div class="count">
        <BaseIconSvoi icon="messages/message" size="s" />
        &nbsp;&nbsp;
        <span>{{ sortedComments.length }}</span>
      </div>
    </div>

    <BaseAsyncWrapper :loadable="commentsLoadable">
      <template #loading>
        <BaseLoader label="Загрузка комментариев" size="s" />
      </template>

      <template #default="{ data: comments }">
        <div v-if="comments.length" class="list">
          <ArticleCommentItem v-for="item in comments" :key="item.id" :comment="item" />
        </div>
        <div v-else>
          <BaseLabel
            theme="potato"
            name="Комментариев пока никто не оставил"
            icon-left="essential/info-circle"
          />
        </div>
      </template>
    </BaseAsyncWrapper>

    <div v-if="isAuth" class="comment-form">
      <BaseInput
        v-model="text"
        label="Добавить комментарий"
        icon="essential/send-2"
        :disabled="isSending"
        @on-click-icon="sendComment"
        @keyup.enter="sendComment"
      />

      <BaseFilePicker
        v-model="filePickerValue"
        accept="image/png,image/jpeg,image/jpg"
        multiple
        type="button"
        :button-text="filePickerButtonText"
        description="png, jpg, jpeg до 6МБ каждое, общий размер до 18МБ"
        :disabled="isSending || uploadedImages.length >= 3"
      />

      <div class="images-preview" :class="{ '-has-images': uploadedImages.length }">
        <div v-for="(preview, index) in imagesPreviewUrls" :key="index" class="image-preview">
          <img :src="preview" :alt="`Превью изображения ${index + 1}`" />
          <button
            type="button"
            class="remove-image"
            :disabled="isSending"
            @click="removeImage(index)"
          >
            <BaseIconSvoi icon="essential/close-circle" size="xs" />
          </button>
        </div>
      </div>
    </div>

    <ClientOnly>
      <BaseModal v-model="isSuccessModal" size="sm">
        <div class="success-content">
          <img class="image" src="~/assets/images/modals/success-check.png" alt="success img" />
          <h3>Спасибо!</h3>
          <p>Ваш комментарий отправлен на модерацию и при успешном прохождении будет опубликован</p>
        </div>
      </BaseModal>
    </ClientOnly>
  </div>
</template>

<script lang="ts" setup>
  import { BaseIconSvoi } from '~/components/base-ui/icon'
  import { BaseInput } from '~/components/base-ui/input'
  import { BaseLoader } from '~/components/base-ui/loader'
  import { BaseLabel } from '~/components/base-ui/label'
  import { BaseModal } from '~/components/base-ui/modal'
  import { BaseFilePicker } from '~/components/base-ui/filepicker'
  import { BaseAsyncWrapper } from '~/components/base-ui/async-wrapper'
  import ArticleCommentItem from './ArticleCommentItem.vue'
  import type { ArticleComment } from '~/models/articles'
  import { toLoadableFromFlags } from '~/shared/utils/loadable'

  interface Props {
    slug: string
    initialComments?: ArticleComment[]
    showComments?: boolean
  }

  const props = withDefaults(defineProps<Props>(), {
    initialComments: () => [],
    showComments: false,
  })

  const emit = defineEmits<{
    (e: 'update-comments'): void
  }>()

  const { $services } = useNuxtApp()
  const { isAuth } = useAuth()
  const { displayError } = useErrors()
  const { isMobile } = useBreakpoint()

  const text = ref('')
  const localComments = ref<ArticleComment[]>([])
  const loading = ref(false)
  const isSending = ref(false)
  const isSuccessModal = ref(false)
  const uploadedImages = ref<File[]>([])
  const imagesPreviewUrls = ref<string[]>([])
  const filePickerValue = ref<File[]>([])
  const intersectionRef = ref<HTMLDivElement>()
  let observer: IntersectionObserver | null = null

  const MAX_FILES = 3
  const MAX_SIZE = 6 * 1024 * 1024

  const filePickerButtonText = computed(() =>
    isMobile.value ? 'Прикрепить фото (до 3)' : 'Прикрепить до 3-ёх изображений'
  )

  const sortedComments = computed(() => [...localComments.value].sort((a, b) => b.id - a.id))

  const commentsLoadable = toLoadableFromFlags(sortedComments, loading)

  watch(filePickerValue, newFiles => {
    if (!newFiles || newFiles.length === 0) {
      return
    }

    const remaining = MAX_FILES - uploadedImages.value.length
    const files = Array.from(newFiles).slice(0, remaining)

    files.forEach(file => {
      if (file.size > MAX_SIZE) {
        return
      }
      if (uploadedImages.value.length >= MAX_FILES) {
        return
      }

      uploadedImages.value.push(file)

      const url = URL.createObjectURL(file)
      imagesPreviewUrls.value.push(url)
    })

    filePickerValue.value = []
  })

  const removeImage = (index: number) => {
    const url = imagesPreviewUrls.value[index]
    if (url) {
      URL.revokeObjectURL(url)
    }

    uploadedImages.value.splice(index, 1)
    imagesPreviewUrls.value.splice(index, 1)
  }

  const resetForm = () => {
    text.value = ''
    imagesPreviewUrls.value.forEach(url => URL.revokeObjectURL(url))
    uploadedImages.value = []
    imagesPreviewUrls.value = []
    filePickerValue.value = []
  }

  const sendComment = () => {
    if (!text.value.trim() && uploadedImages.value.length === 0) {
      return
    }

    isSending.value = true

    displayError(
      $services.articleService.comments
        .addComment(
          props.slug,
          text.value,
          uploadedImages.value.length > 0 ? uploadedImages.value : undefined
        )
        .then(() => {
          resetForm()
          isSuccessModal.value = true
          emit('update-comments')
        })
        .finally(() => {
          isSending.value = false
        })
    )
  }

  const applyComments = () => {
    loading.value = true

    return Promise.resolve()
      .then(() => {
        localComments.value = [...props.initialComments]
      })
      .finally(() => {
        loading.value = false
      })
  }

  const initObserver = () => {
    if (!intersectionRef.value) {
      return
    }

    observer = new IntersectionObserver(
      ([entry]) => {
        if (entry && entry.isIntersecting) {
          applyComments().then(() => destroyObserver())
        }
      },
      { rootMargin: '100px 0px 100px 0px' }
    )

    observer.observe(intersectionRef.value)
  }

  const destroyObserver = () => {
    if (observer) {
      observer.disconnect()
      observer = null
    }
  }

  watch(
    () => props.initialComments,
    value => {
      localComments.value = [...value]
      loading.value = false
    }
  )

  onMounted(initObserver)

  onUnmounted(() => {
    destroyObserver()
    imagesPreviewUrls.value.forEach(url => URL.revokeObjectURL(url))
  })
</script>

<style lang="scss" scoped>
  .article-comments {
    @extend %flex-column;

    gap: 16px;
    max-height: 650px;
    padding: 32px;
    border-radius: 24px;
    background-color: $color-default-white;

    @include vp-767 {
      max-height: 450px;
      padding: 20px;
    }

    > .header {
      display: flex;
      justify-content: space-between;
      position: relative;

      > h2 {
        font-weight: 500;
        font-size: 24px;
        line-height: 32px;
        margin: 0;

        @include vp-767 {
          font-size: 20px;
          line-height: 28px;
        }
      }

      > .count {
        display: flex;
        align-items: center;
        align-self: flex-start;
        font-size: 10px;
        font-weight: 400;
        line-height: 14px;
        color: $color-potato-70;
      }
    }

    > .list {
      @extend %flex-column;

      gap: 8px;
      overflow-y: auto;
      overscroll-behavior: none;
      height: 100%;
      scrollbar-color: $color-potato-20 $color-default-white;
      scrollbar-width: thin;

      &::-webkit-scrollbar {
        width: 4px;
        background-color: $color-default-white;
      }

      &::-webkit-scrollbar-thumb {
        background-color: $color-potato-20;
        border-radius: 4px;
      }
    }

    > .comment-form {
      @extend %flex-column;

      gap: 12px;
    }

    > .comment-form > .images-preview {
      display: flex;
      gap: 12px;
      min-height: 0;
      max-height: 0;
      overflow: hidden;
      opacity: 0;
      transition: all $trans-default;

      &.-has-images {
        min-height: 96px;
        max-height: 96px;
        opacity: 1;
      }
    }

    > .comment-form > .images-preview > .image-preview {
      position: relative;
      display: inline-block;
      max-width: 120px;
      animation: fade-in-scale $trans-default;

      > img {
        width: 100%;
        height: 80px;
        object-fit: cover;
        border-radius: 8px;
        border: 1px solid $color-potato-20;
        transition: opacity 0.2s ease;
      }

      > .remove-image {
        position: absolute;
        top: 4px;
        right: 4px;
        background: rgb(0 0 0 / 70%);
        border: none;
        border-radius: 50%;
        width: 20px;
        height: 20px;
        display: flex;
        align-items: center;
        justify-content: center;
        cursor: pointer;
        color: $color-default-white;
        transition: background-color 0.2s;

        &:hover {
          background: rgb(0 0 0 / 90%);
        }

        &:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }
      }
    }

    .success-content {
      @extend %flex-column;

      align-items: center;
      text-align: center;
      gap: 12px;
      padding: 24px;

      > h3 {
        font-size: 24px;
        font-weight: 500;
        line-height: 32px;
        margin: 0;
      }

      > p {
        font-size: 16px;
        line-height: 24px;
        color: $color-potato-60;
        margin: 0;
      }
    }
  }

  @keyframes fade-in-scale {
    from {
      opacity: 0;
      transform: scale(0.8);
    }

    to {
      opacity: 1;
      transform: scale(1);
    }
  }
</style>
